import { useState } from 'react';
import { useQuotes } from '../../hooks/useQuotes';
import { Quote, QuoteStatus } from '../../mocks/quotes';
import QuoteCard from './components/QuoteCard';
import QuotePDFPreview from './components/QuotePDFPreview';
import QuoteBuilder from './components/QuoteBuilder';
import SendQuoteModal from './components/SendQuoteModal';
import QuoteAnalytics from './components/QuoteAnalytics';

const statusFilters: { label: string; value: QuoteStatus | 'All' }[] = [
  { label: 'All Quotes', value: 'All' },
  { label: 'Draft', value: 'Draft' },
  { label: 'Sent', value: 'Sent' },
  { label: 'Accepted', value: 'Accepted' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Expired', value: 'Expired' },
];

export default function QuotesPage() {
  const { quotes, loading, createQuote, updateQuoteStatus } = useQuotes();
  const [tab, setTab] = useState<'quotes' | 'builder' | 'analytics'>('quotes');
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'All'>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [previewQuote, setPreviewQuote] = useState<Quote | null>(null);
  const [sendQuote, setSendQuote] = useState<Quote | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editQuote, setEditQuote] = useState<Quote | null>(null);

  const filteredQuotes = quotes
    .filter((q) => (statusFilter === 'All' ? true : q.status === statusFilter))
    .filter(
      (q) =>
        q.leadName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.destination.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const kpis = [
    {
      label: 'Total Quotes',
      value: quotes.length.toString(),
      icon: 'ri-file-list-3-line',
      color: 'bg-amber-100 text-amber-600',
    },
    {
      label: 'Acceptance Rate',
      value: quotes.filter((q) => ['Sent', 'Accepted', 'Rejected', 'Expired'].includes(q.status)).length > 0
        ? `${Math.round((quotes.filter((q) => q.status === 'Accepted').length / quotes.filter((q) => ['Sent', 'Accepted', 'Rejected', 'Expired'].includes(q.status)).length) * 100)}%`
        : '0%',
      icon: 'ri-checkbox-circle-line',
      color: 'bg-emerald-100 text-emerald-600',
    },
    {
      label: 'Total Value',
      value: `AED ${quotes.reduce((s, q) => s + q.totalAmount, 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}`,
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-teal-100 text-teal-600',
    },
    {
      label: 'Pending Quotes',
      value: quotes.filter((q) => q.status === 'Sent').length.toString(),
      icon: 'ri-timer-line',
      color: 'bg-sky-100 text-sky-600',
      badge: quotes.filter((q) => q.status === 'Sent').length > 0 ? quotes.filter((q) => q.status === 'Sent').length : null,
    },
  ];

  const statusCounts = {
    All: quotes.length,
    Draft: quotes.filter((q) => q.status === 'Draft').length,
    Sent: quotes.filter((q) => q.status === 'Sent').length,
    Accepted: quotes.filter((q) => q.status === 'Accepted').length,
    Rejected: quotes.filter((q) => q.status === 'Rejected').length,
    Expired: quotes.filter((q) => q.status === 'Expired').length,
  };

  const handleCreateQuote = async (draft: Partial<Quote>) => {
    await createQuote(draft);
    setBuilderOpen(false);
    setEditQuote(null);
    if (draft.status === 'Sent') {
      setSendQuote(null);
    }
  };

  const handleSendQuote = (quoteId: string) => {
    updateQuoteStatus(quoteId, 'Sent');
  };

  const handleConvert = (quote: Quote) => {
    alert(`Converting quote ${quote.id} to booking for ${quote.leadName}!\n\n(This would open the new booking form pre-filled with quote data)`);
  };

  const handleDuplicate = (quote: Quote) => {
    setEditQuote(quote);
    setBuilderOpen(true);
  };

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Quotes</h1>
              <p className="text-sm text-gray-500 mt-0.5">Create and manage travel quotes for your leads</p>
            </div>
            <button
              onClick={() => {
                setBuilderOpen(true);
                setEditQuote(null);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base" /> New Quote
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${kpi.color} flex-shrink-0`}>
                  <i className={`${kpi.icon} text-xl`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-0.5">{kpi.label}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold text-gray-800">{kpi.value}</p>
                    {kpi.badge && (
                      <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-600 text-xs font-bold">
                        {kpi.badge}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit">
            {[
              { id: 'quotes' as const, label: 'All Quotes', icon: 'ri-file-list-3-line' },
              { id: 'builder' as const, label: 'Create Quote', icon: 'ri-add-box-line' },
              { id: 'analytics' as const, label: 'Analytics', icon: 'ri-bar-chart-box-line' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  tab === t.id
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${t.icon} text-base`} /> {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content: All Quotes */}
          {tab === 'quotes' && (
            <div className="space-y-4">
              {/* Filters & Search */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 overflow-x-auto flex-1">
                  {statusFilters.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setStatusFilter(f.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                        statusFilter === f.value
                          ? 'bg-amber-100 text-amber-700 border border-amber-200'
                          : 'bg-white text-gray-500 border border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      {f.label}
                      {f.value !== 'All' && statusCounts[f.value] > 0 && (
                        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${statusFilter === f.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                          {statusCounts[f.value]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="relative w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search quotes..."
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
                  />
                </div>
              </div>

              {/* Quotes Grid */}
              {filteredQuotes.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {filteredQuotes.map((quote) => (
                    <QuoteCard
                      key={quote.id}
                      quote={quote}
                      onPreview={setPreviewQuote}
                      onSend={setSendQuote}
                      onConvert={handleConvert}
                      onDuplicate={handleDuplicate}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-search-line text-2xl text-gray-300" />
                  </div>
                  <p className="text-gray-400">No quotes found</p>
                </div>
              )}
            </div>
          )}

          {/* Tab Content: Create Quote */}
          {tab === 'builder' && (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-draft-line text-2xl text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Create New Quote</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                Build a detailed quote with services, pricing, and terms, then send it to your lead via email or WhatsApp.
              </p>
              <button
                onClick={() => {
                  setBuilderOpen(true);
                  setEditQuote(null);
                }}
                className="px-5 py-3 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-2" /> Start Building
              </button>
            </div>
          )}

          {/* Tab Content: Analytics */}
          {tab === 'analytics' && <QuoteAnalytics quotes={quotes} />}
        </div>
      </div>

      {/* Modals */}
      {previewQuote && (
        <QuotePDFPreview
          quote={previewQuote}
          onClose={() => setPreviewQuote(null)}
          onSend={previewQuote.status === 'Draft' || previewQuote.status === 'Sent' ? () => {
            setSendQuote(previewQuote);
            setPreviewQuote(null);
          } : undefined}
        />
      )}
      {sendQuote && (
        <SendQuoteModal
          quote={sendQuote}
          onClose={() => setSendQuote(null)}
          onSent={handleSendQuote}
        />
      )}
      {builderOpen && (
        <QuoteBuilder
          initialQuote={editQuote}
          onClose={() => {
            setBuilderOpen(false);
            setEditQuote(null);
          }}
          onSave={handleCreateQuote}
        />
      )}
    </div>
  );
}
